import React, { useEffect, useRef, useState } from "react";
import { Box, Skeleton } from "@mui/material";
import CheckroomOutlinedIcon from "@mui/icons-material/CheckroomOutlined";
import { getNmlSuzhouPhotoBlob } from "../../api/NmlSuzhou/nmlSuzhouApi";

const cache = new Map();

export default function NmlProductPhoto({ productId, alt, onOpen }) {
  const rootRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [src, setSrc] = useState(cache.get(productId) || null);
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "280px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !productId || cache.has(productId)) {
      if (cache.has(productId)) setSrc(cache.get(productId));
      return undefined;
    }
    let alive = true;
    getNmlSuzhouPhotoBlob(productId)
      .then((url) => {
        if (!alive) return;
        if (!url) {
          setEmpty(true);
          return;
        }
        cache.set(productId, url);
        setSrc(url);
      })
      .catch(() => {
        if (alive) setEmpty(true);
      });
    return () => {
      alive = false;
    };
  }, [visible, productId]);

  const frameSx = {
    width: 48,
    height: 64,
    borderRadius: 2.25,
    overflow: "hidden",
    flexShrink: 0,
    bgcolor: "action.hover",
    border: "1px solid",
    borderColor: "divider",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 18px rgba(15,23,42,0.12)",
  };

  if (!visible || (!src && !empty)) {
    return (
      <Box ref={rootRef} sx={frameSx}>
        <Skeleton variant="rounded" width={48} height={64} />
      </Box>
    );
  }

  if (empty || !src) {
    return (
      <Box ref={rootRef} sx={{ ...frameSx, color: "text.disabled" }}>
        <CheckroomOutlinedIcon fontSize="small" />
      </Box>
    );
  }

  return (
    <Box
      ref={rootRef}
      component="img"
      src={src}
      alt={alt || ""}
      onClick={(e) => {
        e.stopPropagation();
        onOpen?.(src, alt);
      }}
      sx={{
        width: 48,
        height: 64,
        borderRadius: 2.25,
        objectFit: "cover",
        flexShrink: 0,
        cursor: "zoom-in",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 6px 16px rgba(15,23,42,0.12)",
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
        "&:hover": {
          transform: "translateY(-1px) scale(1.03)",
          boxShadow: "0 10px 22px rgba(15,23,42,0.18)",
        },
      }}
    />
  );
}
